import { graphql } from "gatsby";
import PropTypes from "prop-types";
import React from "react";
import { formatDate } from "../../../helpers/dates";
import { mediaObjectRenderer } from "../../../helpers/media-object-renderer";
import { ChervonRight } from "../../../icons";
import AutoLink from "../../common/links/AutoLink";
import { useGlobalPageContext } from "../../layout/GlobalPageContext";

export const fragment = graphql`
  fragment postPreviewFragment on ContentfulPost {
    id
    title
    slug
    readingTime
    date
    featuredPost
    category {
      title
      slug
    }
    tags {
      slug
      name
    }
    node_locale
    mainImage {
      ...MediaItemFragment
    }
  }
`;
/**
 * NB: the card used to be with fixed sizes of both rows. The second row (text content) is changed to be auto size
 * and the surrounding grid containing the cards should always have `grid-auto-rows-fr` to force all cards to be the same size (whatever that is).
 */
const PostPreview = React.forwardRef(
  (
    { id, slug, categoryName, mainImage, title, date, readingTime, readText },
    ref
  ) => {
    const { lang } = useGlobalPageContext();

    return (
      <div
        key={id}
        className="bg-card-gradient group card-shadow rounded-[18px] transition-all ease-in duration-150 overflow-clip w-full hover:card-shadow-hover cursor-pointer"
        ref={ref}
      >
        <AutoLink
          className="grid h-full grid-rows-[250px_auto] hover:no-underline"
          href={slug}
        >
          {/* image */}
          <div className="row-start-1 h-[250px] relative">
            {mediaObjectRenderer(mainImage, "h-full w-full")}
            <div className="absolute top-0 left-0 flex flex-row items-center justify-center w-full h-full gap-2 transition-all duration-150 ease-in opacity-0 bg-grey-800 bg-opacity-60 text-grey-100 group-hover:opacity-100">
              {readText}
              <ChervonRight className="w-3 h-3" />
            </div>
          </div>

          {/* text content */}
          <div className="row-start-2 p-8 grid grid-rows-[minmax(0,1fr)_auto] grid-flow-col">
            <div>
              {categoryName && (
                <div className="mb-4 typo-button-text text-grey-600">
                  {categoryName}
                </div>
              )}

              <h3 className="typo-h3 line-clamp-3 [@media(max-width:320px)]:break-all">
                {title}
              </h3>
            </div>

            {(date || readingTime) && (
              <span className={`typo-body-2 text-grey-600 mt-6`}>
                {date && formatDate(new Date(date), lang)}
                {readingTime && <b>{` - ${readingTime}`}</b>}
              </span>
            )}
          </div>
        </AutoLink>
      </div>
    );
  }
);

PostPreview.propTypes = {
  id: PropTypes.string,
  slug: PropTypes.string,
  categoryName: PropTypes.string,
  categoryUrl: PropTypes.string,
  mainImage: PropTypes.object,
  title: PropTypes.string,
  date: PropTypes.string,
  readingTime: PropTypes.string,
  readText: PropTypes.string,
};

export default PostPreview;
